import React, {useEffect} from "react";
import {connect} from "react-redux";
import {Dispatch} from "redux";
import {AppState} from "../RootReducer";
import {ContentModelName, FooterPayload, InfoPage} from "../contenful/types";
import {fetchAndParseContent, fetchContent, fetchInfoPage} from "../contenful/ContentfulActions";
import {Spinner} from "../ui/Spinner";
import {BLOCKS} from '@contentful/rich-text-types';
import {documentToReactComponents} from "@contentful/rich-text-react-renderer";
import Root from "../layout/Root";
import Page from "../layout/Page";
import "./styles/InfoPageView.scss";
import {RouteComponentProps} from "react-router";
import {getContainer} from "../ioc/IOCSetup";
import {t} from "../i18n";
import {Settings} from "../http/protocol";
import MetaHelmet from "../utils/MetaHelmet";
import DynamicContentView from "../landingpage/pagesection/DynamicContentView";
import {getLoggedIn} from "../selectors/user-selectors";
import {formatUrl} from "../utils/UrlUtils";
import ReactMarkdown from "react-markdown";
import {NormalComponents, ReactMarkdownProps, ReactNode, SpecialComponents} from "react-markdown/src/ast-to-react";
import {OptionalLink} from "../landingpage/pagesection/HeroBannerTwoColumnsComponent";

interface InfoPageComponentProps {
    infoPage?: InfoPage;
    loggedIn?: boolean;
}

export const toHeading = (str? : string) => {
    if(!str) {
        return "";
    }
    return str.toLocaleLowerCase().replaceAll(" ", "").replaceAll(".","");
}

export const InfoPageMarkdownRenderComponents : Partial<Omit<NormalComponents, keyof SpecialComponents> & SpecialComponents> = {
    a : (props: React.ClassAttributes<HTMLAnchorElement> & React.AnchorHTMLAttributes<HTMLAnchorElement> & ReactMarkdownProps ) : ReactNode  => {
        return <OptionalLink to={props.href} >{props.children}</OptionalLink>
    },
    p : (props: React.ClassAttributes<HTMLParagraphElement> & React.HTMLAttributes<HTMLParagraphElement> & ReactMarkdownProps ) : ReactNode  => {
        return <span>{props.children}</span>
    },
    h1 : (props : React.ClassAttributes<HTMLHeadingElement> & React.HTMLAttributes<HTMLHeadingElement> & ReactMarkdownProps) : ReactNode => {
        let id = "";
        if(typeof props.children === "string") {
            id = "title"+toHeading(props.children);
        }
        return <h1 id={id}>{props.children}</h1>
    },
    h2 : (props : React.ClassAttributes<HTMLHeadingElement> & React.HTMLAttributes<HTMLHeadingElement> & ReactMarkdownProps) : ReactNode => {
        let id = "";
        if(typeof props.children === "string") {
            id = "title"+toHeading(props.children);
        }
        return <h2 id={id}>{props.children}</h2>
    },
    h3 : (props : React.ClassAttributes<HTMLHeadingElement> & React.HTMLAttributes<HTMLHeadingElement> & ReactMarkdownProps) : ReactNode => {
        let id = "";
        if(typeof props.children === "string") {
            id = "title"+toHeading(props.children);
        }
        return <h3 id={id}>{props.children}</h3>
    },
    img: (props: React.ClassAttributes<HTMLImageElement> & React.ImgHTMLAttributes<HTMLImageElement> & ReactMarkdownProps): ReactNode => {
        const { alt, src } = props;

        const dimensions = /=(\d+)x(\d+)/;
        const size = src ? src.match(dimensions) : null;
        const parsedSrc = src ? src.replace(dimensions, "").trim() : "";
        const imgAlt = alt;
        
        const width = size && !isNaN(Number(size[1])) ? size[1] : undefined;
        const height = size && !isNaN(Number(size[2])) ? size[2] : undefined;
        const sanitizedAlt = imgAlt || "Image";

        return <img alt={sanitizedAlt} width={width} height={height} src={parsedSrc} />;
   },
}
export const documentToReactComponentsLocal = (rich : any, options : any) => {
    if(typeof rich === "string") {
        return <ReactMarkdown components={InfoPageMarkdownRenderComponents}>{rich}</ReactMarkdown>
    }
    return documentToReactComponents(rich, options);
}
export const richTextOptions = {
    renderNode: {
        [BLOCKS.HEADING_1] : (node : any, children : any) => {

        },
        [BLOCKS.HEADING_2] : (node : any, children : any) => {

            let id = "";
            if((node?.content?.length ?? 0) == 1  && node.content?.[0]?.nodeType == "text" && typeof(node.content?.[0]?.value) == "string") {
                id = "title"+toHeading(node.content[0].value);
            }
            return <h2 id={id}>{children}</h2>
        },
        [BLOCKS.HEADING_3] : (node : any, children : any) => {

            let id = "";
            if((node?.content?.length ?? 0) == 1  && node.content?.[0]?.nodeType == "text" && typeof(node.content?.[0]?.value) == "string") {
                id = "title"+toHeading(node.content[0].value);
            }
            return <h3 id={id}>{children}</h3>
        },
        [BLOCKS.EMBEDDED_ASSET]: (node:any) => {
            const { title, description, file } = node.data.target.fields;
            const mimeType = file.contentType;
            const mimeGroup = mimeType.split('/')[0];

            if(mimeGroup == 'image') {
                return <img
                    title={ title ? title : null}
                    alt={description ?  description : "info"}
                    src={file.url}
                />;
            } else {
                return <span style={{backgroundColor: 'red', color: 'white'}}> {mimeType} embedded asset </span>;
            }
        }
    }
};

export const InfoPageComponent = (props: InfoPageComponentProps) => {
    useEffect(() => {
        const cardBodyClass = document.querySelectorAll<HTMLElement>('.expandable-accordion');
        const arrow = document.querySelectorAll<HTMLElement>('.arrow-accordion');
        for (var i = 0; i < cardBodyClass.length; i++) {
            cardBodyClass[i].style.height = '0px'
            cardBodyClass[i].style.visibility = 'hidden'
            cardBodyClass[i].style.maxHeight = '0px'
            cardBodyClass[i].classList.remove('expanded')
         }
         for (var i = 0; i < arrow.length; i++) {
            arrow[i].classList.remove('show')
         }
    });

    let infoPageId = props.infoPage?._id;
    if(!infoPageId) {
        infoPageId = props.infoPage?.id;
    }
    let seoTitle = "";
    let seoDescription = "";
    let seoImageURL = "";
    if(props.infoPage && props.infoPage.seo) {
        seoTitle = props.infoPage.seo.title || "";
        seoDescription = props.infoPage.seo.description || "";
        seoImageURL = formatUrl(props.infoPage.seo.image?.fields.file.url) || "";
    }
    let locale = getContainer().getSettingsService().getActiveLocale().code;
    return (
        <Root footer={true} className={props.infoPage?.containerCss}>
            <MetaHelmet title={seoTitle} description={seoDescription} ogTitle={seoTitle} ogDescription={seoDescription} ogImage={seoImageURL} lang={locale} />
            <Page>
                <div className={`infoPageView ${infoPageId}`}>
                    <div className="infoPage-container-outer">
                        <div className={`infoPage-container`}>
                            {
                                props.infoPage && (
                                    documentToReactComponentsLocal(props.infoPage.body as any, richTextOptions)
                                )
                            }
                        </div>
                        <div className={`infoAccordion`}>
                            <DynamicContentView key={infoPageId} dynamicContent={props.infoPage && props.infoPage.dynamicContent}
                                infoPageId={props.infoPage && props.infoPage.id} loggedIn={props.loggedIn}
                            />
                        </div>
                    </div>
                </div>
            </Page>
        </Root>
    )
}

type AllProps = PropsFromState & PropsFromDispatch & OtherProps;

export class InfoPageView extends React.Component<AllProps, { entries? : any[], loading : boolean}>{


    constructor(props: AllProps) {
        super(props);
        this.state = { loading : true }
    }
    componentDidMount(): void {
        this.loadContent();
    }
    loadContent() {
        let params = this.props.match.params;
        if(params.page){
            fetchInfoPage(params.page, this.props.lng).then((i : InfoPage[]) => {
                this.setState({ entries : i, loading : false})
                if(i.length == 0) {
                    this.props.history.push("/404");
                }
            }).catch((e) => {
                console.log("Unable to find page " + params.page,e);
                this.props.history.push("/404");
            })
        }
    }
    componentDidUpdate(prevProps: AllProps, prevState : any): void {
        if(this.props.lng && this.props.lng != prevProps.lng || this.props.match.params.page != prevProps.match.params.page) {
            this.loadContent();
        }
        if(this.state.entries != prevState.entries) {
            if(this.props.location.hash) {
                const yOffset = -70;
                const element =  document.querySelector(this.props.location.hash);

                if(element) {
                    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({top: y, behavior: 'auto'});
                }
            } else {
                window.scrollTo({top : 0 });
            }
        } else if(this.props.location.hash != prevProps.location.hash) {
            if(!this.props.location.hash) {
                window.scrollTo({top : 0 });
            } else {
                const yOffset = -70;
                const element =  document.querySelector(this.props.location.hash);

                if(element) {
                    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({top: y, behavior: 'auto'});
                }
            }

        }

    }

    render() {
        let params = this.props.match.params;
        const subtitleFromParams = params.page && params.page.charAt(0).toUpperCase() + params.page.slice(1).toLowerCase();
        const title:string = this.props.settings && this.props.settings.title || "";
        const subtitle:string = subtitleFromParams || t("meta.infoPages.title") || "";
        const fullTitle:string = `${title}${subtitle.length > 0 ? " - "+subtitle : ""}`;
        const description:string = t("meta.infoPages.description") || "";
        const keywords:string = t("meta.infoPages.keywords") || "";

        return(
            <div>
                <MetaHelmet title={fullTitle} description={description} keywords={keywords} />
                {
                    (this.state.loading || !this.state.entries || this.state.entries.length == 0)
                        ? <Spinner/>
                        : <InfoPageComponent key={params.page ?? "default"} infoPage={this.state.entries[0] } loggedIn={this.props.loggedIn}/>
                }
            </div>
        )
    }
}

interface OtherProps extends RouteComponentProps<{page? : string}> {
}

interface PropsFromState {
    isFetching?: boolean;
    footer?: FooterPayload;
    error?: string;
    lng: string;
    settings?: Settings;
    loggedIn? : boolean;
}

interface PropsFromDispatch {
    fetchContent: typeof fetchContent;
}


const mapStateToProps = (state: AppState) => {
    return {
        isFetching: state.contentful.isFetchingMultiple,
        error: state.contentful.error,
        footer: state.contentful.footer,
        lng: getContainer().getSettingsService().getActiveLocale().code,
        settings : state.settings.settings,
        loggedIn : getLoggedIn(state)
    }
}

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        fetchContent: (contentModelName: ContentModelName, locale?: string) => dispatch(fetchAndParseContent(contentModelName, locale) as any)
    }
}


export default connect(mapStateToProps, mapDispatchToProps)(InfoPageView)